import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import handleRolesOptions from '../../common/FieldsList/utils/handleRolesOptions';
import CancelPendingInvitationsPopup from '../CancelPendingInvitationsPopup';
import InvitationButton from '../../InvitationBox/InviteButton';
import { CARE_ROLE, PATIENT } from '../../../constants/props';
import getSimpleFormProps from '../utils/getSimpleFormProps';
import SimpleFormPopup from '../../common/SimpleFormPopup';
import { useFamilyCircle } from '../utils/useFamilySircle';
import { useSimpleForm } from '../utils/useSimpleForm';
import DiscardModal from '../../common/DiscardModal';
import PopoverMenu from '../../common/PopoverMenu';
import { CHAT } from '../../../constants/strings';
import FamilyCircle from '../FamilyCircle';
import Loader from '../../common/Loader';
import { enhance } from './enhance';

function FamilyCircleWrapper({
  isProvider,
  changeRole,
  hideHeader,
  shouldClear,
  targetUser,
  createdUser,
  targetUserId,
  familyCircle,
  getMenuItems,
  loadingCircle,
  circleLoading,
  clearRequests,
  showTargetUser,
  hasFixedHeight,
  sendPatientInvite,
  inviteMemberErrors,
  removeFamilyMember,
  inviteFamilyMember,
  invitePatientError,
  showInvitationButton,
  pendingFamilyMembers,
  handleTargetUserInvite,
  intl: { formatMessage },
}) {
  const {
    getItems,
    circleState,
    handleClearState,
    handleOpenPopover,
    handleOpenSimpleFormPopup,
  } = useFamilyCircle({
    getMenuItems,
    createdUser,
    clearRequests,
    handleTargetUserInvite,
  });

  const {
    user,
    anchorEl,
    actionType,
    isModalOpen,
    isPopoverOpen,
    isSimpleFormOpen,
    isCancelPopupOpen,
  } = circleState;

  const memberId = useMemo(() => get(user, 'id'), [user]);

  const {
    setFormFields,
    useCustomFields,
    simpleFormHandlers,
    simpleFormInitState,
  } = useSimpleForm({
    memberId,
    targetUser,
    changeRole,
    sendPatientInvite,
    inviteFamilyMember,
  });

  useEffect(() => {
    if (shouldClear) handleClearState();
  }, [shouldClear]);

  const list = useMemo(() => {
    const circleList = [...familyCircle, ...pendingFamilyMembers];
    if (showTargetUser) {
      const targetUserItem = {
        ...targetUser,
        isTargetUser: true,
        pending: !createdUser,
        role: PATIENT,
      };
      return [targetUserItem, ...circleList];
    }
    return circleList;
  }, [
    showTargetUser,
    targetUser,
    familyCircle,
    pendingFamilyMembers,
    createdUser,
  ]);

  const customRoleOptions =
    showTargetUser && createdUser
      ? handleRolesOptions(formatMessage, {
          PATIENT,
          ...CARE_ROLE,
        })
      : null;

  const simpleFormProps = getSimpleFormProps(
    simpleFormHandlers(),
    actionType,
    user,
    formatMessage,
    {
      patientInvite: showTargetUser && createdUser,
      usePatientFields: useCustomFields,
    },
  );

  if (loadingCircle) return <Loader />;

  return (
    <Fragment>
      <FamilyCircle
        isProvider={isProvider}
        onInvite={handleOpenSimpleFormPopup}
        onOpen={handleOpenPopover}
        formatMessage={formatMessage}
        relatedPatientId={targetUserId}
        list={list}
        title={formatMessage(CHAT.titles.familyChatTab)}
        type="family-member"
        hideHeader={hideHeader}
        hasFixedHeight={hasFixedHeight}
      />

      <If condition={isPopoverOpen}>
        <PopoverMenu
          size="small"
          isOpen
          anchorEl={anchorEl}
          items={getItems}
          key={anchorEl}
          onClose={handleClearState}
        />
      </If>
      <If condition={isModalOpen}>
        <DiscardModal
          isOpened
          onSubmit={() => removeFamilyMember(memberId)}
          onCancel={handleClearState}
          title={formatMessage({ id: 'circles.family.removeQuestion' })}
        />
      </If>
      <If condition={isSimpleFormOpen}>
        <SimpleFormPopup
          {...simpleFormProps}
          loading={circleLoading}
          errors={useCustomFields ? invitePatientError : inviteMemberErrors}
          onClose={handleClearState}
          customRoleOptions={customRoleOptions}
          onChange={state => setFormFields(state)}
          initialData={simpleFormInitState}
          key={useCustomFields}
        />
      </If>
      <If condition={isCancelPopupOpen}>
        <CancelPendingInvitationsPopup
          targetUserId={targetUserId}
          user={user}
          onClose={handleClearState}
          formatMessage={formatMessage}
        />
      </If>
      <If condition={showInvitationButton}>
        <InvitationButton
          formatMessage={formatMessage}
          onClick={handleOpenSimpleFormPopup}
        />
      </If>
    </Fragment>
  );
}

FamilyCircleWrapper.propTypes = {
  circleLoading: PropTypes.bool,
  isProvider: PropTypes.bool,
  changeRole: PropTypes.func,
  hideHeader: PropTypes.bool,
  createdUser: PropTypes.bool,
  shouldClear: PropTypes.bool,
  loadingCircle: PropTypes.bool,
  targetUser: PropTypes.object,
  targetUserId: PropTypes.number,
  familyCircle: PropTypes.array,
  getMenuItems: PropTypes.func,
  clearRequests: PropTypes.func,
  getCircleOfCare: PropTypes.func,
  removeFamilyMember: PropTypes.func,
  inviteFamilyMember: PropTypes.func,
  showInvitationButton: PropTypes.bool,
  pendingFamilyMembers: PropTypes.array,
  handleTargetUserInvite: PropTypes.func,
  showTargetUser: PropTypes.bool,
  hasFixedHeight: PropTypes.bool,
  intl: PropTypes.object,
  inviteMemberErrors: PropTypes.object,
  invitePatientError: PropTypes.object,
  sendPatientInvite: PropTypes.func,
};

export default enhance(FamilyCircleWrapper);



// WEBPACK FOOTER //
// ./src/components/CircleOfCare/FamilyCircleWrapper/index.jsx